import React, { useState, useEffect } from "react";
import { Container, Row, Col, Spinner } from "react-bootstrap";
import { fetchPublicBlogs } from "../utils/blogApi";
import BlogCard from "./BlogCard";
import BlogFeatured from "./BlogFeatured";

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [featuredBlog, setFeaturedBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Blog | BBR Boat Rentals - Boating Tips, Fishing Guides & News";
    
    // Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      "content",
      "Read the latest boating tips, fishing guides, tournament news, and expert advice from BBR Boat Rentals. Your source for bass boat rental insights."
    );

    // Meta Keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement("meta");
      metaKeywords.name = "keywords";
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute(
      "content",
      "boat rentals, bass boat, fishing tips, boating guides, tournament fishing, BBR, boat rental blog"
    );

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${window.location.origin}/blog`;

    // Open Graph Tags
    const ogTags = [
      { property: "og:title", content: "Blog | BBR Boat Rentals" },
      { property: "og:description", content: "Read the latest boating tips, fishing guides, and expert advice from BBR Boat Rentals." },
      { property: "og:url", content: `${window.location.origin}/blog` },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "BBR Boat Rentals" },
    ];

    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    });

    // JSON-LD for Blog listing page
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "BBR Boat Rentals Blog",
      description: "Boating tips, fishing guides, tournament news, and expert advice from BBR Boat Rentals.",
      url: `${window.location.origin}/blog`,
      publisher: {
        "@type": "Organization",
        name: "BBR Boat Rentals",
        logo: {
          "@type": "ImageObject",
          url: `${window.location.origin}/assets/frontend/images/bbr-logo.png`,
        },
      },
    };

    let script = document.getElementById("blog-list-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.id = "blog-list-jsonld";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);

    return () => {
      // Cleanup on unmount
      const script = document.getElementById("blog-list-jsonld");
      if (script) script.remove();
      document.querySelectorAll('meta[property^="og:"]').forEach((el) => el.remove());
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  useEffect(() => {
    loadBlogs();
  }, [page]);

  const loadBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPublicBlogs({ page, limit: 10 });
      if (data.success) {
        if (page === 1 && data.blogs.length > 0) {
          setFeaturedBlog(data.blogs[0]);
          setBlogs(data.blogs.slice(1));
        } else {
          setBlogs(data.blogs);
        }
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error("Error loading blogs:", err);
      setError("Failed to load blogs. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="blog-page-loading">
        <Spinner animation="border" variant="dark" />
      </div>
    );
  }

  if (error) {
    return (
      <Container className="blog-page-error">
        <p>{error}</p>
        <button onClick={loadBlogs} className="btn btn-dark">
          Try Again
        </button>
      </Container>
    );
  }

  return (
    <section className="blog-page">
      <div className="blog-page-hero">
        <Container style={{ maxWidth: "1200px" }}>
          <h1 className="blog-page-title">Blog</h1>
          <p className="blog-page-subtitle">
            Latest news, tips, and guides from BBR Boat Rentals
          </p>
        </Container>
      </div>

      <Container style={{ maxWidth: "1200px" }} className="blog-page-content">
        {/* Featured Blog */}
        {page === 1 && featuredBlog && (
          <div className="blog-page-featured-section">
            <BlogFeatured blog={featuredBlog} />
          </div>
        )}

        {/* Blog Grid */}
        {blogs.length > 0 ? (
          <>
            <h2 className="blog-section-heading">
              {page === 1 ? "Recent Posts" : "All Posts"}
            </h2>
            <Row className="blog-grid">
              {blogs.map((blog) => (
                <Col key={blog.id} lg={4} md={6} sm={12} className="mb-4">
                  <BlogCard blog={blog} />
                </Col>
              ))}
            </Row>
          </>
        ) : (
          page !== 1 && (
            <div className="text-center py-5">
              <p>No more posts to show.</p>
            </div>
          )
        )}

        {/* No blogs at all */}
        {!featuredBlog && blogs.length === 0 && (
          <div className="text-center py-5">
            <h3>No blog posts yet</h3>
            <p>Check back soon for new content!</p>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="blog-pagination">
            <button
              className="blog-pagination-btn"
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
            >
              ← Previous
            </button>
            <div className="blog-pagination-pages">
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(
                (p) => (
                  <button
                    key={p}
                    className={`blog-pagination-page ${
                      p === page ? "active" : ""
                    }`}
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </button>
                )
              )}
            </div>
            <button
              className="blog-pagination-btn"
              disabled={page >= pagination.totalPages}
              onClick={() => handlePageChange(page + 1)}
            >
              Next →
            </button>
          </div>
        )}
      </Container>
    </section>
  );
}
